import type {
  ActionInteraction,
  DeriveInteractionProps,
  InlineNavigationTarget,
  InteractionMap,
  ResolvedNavigationTarget,
  VersionString,
  ViewID,
} from "@bestowinc/tinkertoy-core";
import {
  isActionInteraction,
  isNavigationInteraction,
  isRefreshInteraction,
} from "@bestowinc/tinkertoy-core";
import { compile } from "path-to-regexp";

import { submitViewInteraction } from "../actions/submit-view-interaction";

export type InteractionHandlerDeps = {
  /**
   * Identity of the view that produced the loader response. Threaded into
   * every action-interaction call so the server can validate the request
   * against the manifest pinned for `viewId`.
   */
  viewId: ViewID;
  viewVersion: VersionString;
  /** Base URL for the Tinkertoy server `/action` endpoint. */
  apiBaseUrl: string;
  /**
   * Server-resolved navigation targets, keyed by interaction name. Each
   * `view` entry carries the target's route template (still containing
   * `:name` tokens) plus the verbatim `query` map; this hook compiles the
   * URL at click time using {@link currentPathParams} merged with whatever
   * the caller hands the handler.
   *
   * Navigation interactions whose target the server could not resolve
   * (unknown `viewId` / `branchId`) are intentionally omitted; calling
   * such a handler throws a clear error rather than silently navigating
   * somewhere wrong.
   */
  resolvedInteractions: Record<string, ResolvedNavigationTarget>;
  /**
   * The current request's matched path params, as emitted by the server's
   * loader. Used as the default-fill for `:name` tokens in navigation
   * routes — caller-supplied params win on collision.
   */
  currentPathParams: Record<string, string>;
  /** React Router navigate (typically from {@link useTinkertoy}). */
  navigate: (to: string, options?: { replace?: boolean }) => void;
  /**
   * Navigate one entry back in history through React Router (typically
   * from {@link useTinkertoy}). Used for `kind: "back"` navigation
   * targets — calling `window.history.back()` directly would bypass
   * React Router's history abstraction and break `MemoryRouter` (used
   * in tests) plus v7's transition semantics.
   */
  goBack: () => void;
  /** Trigger a route revalidation (re-runs the loader without changing the URL). */
  revalidate: () => void;
};

/**
 * Turn a view's `interactions` map into the component-facing handler map
 * passed to `view.modules` / `ModulesConfig.interactions`.
 *
 * Wiring:
 * - **Navigation** — looks up the server-resolved target on
 *   `deps.resolvedInteractions[key]` and compiles the URL at click time
 *   from the route template + the merge of `currentPathParams` and any
 *   caller-supplied params. Internal (`view`) targets call `navigate(url)`
 *   (and merge `window.location.search` when `persistSearch` is true);
 *   external targets call `window.location.assign`; `back` calls
 *   `deps.goBack()` (the React Router `navigate(-1)` wrapper — see the
 *   {@link InteractionHandlerDeps.goBack} field for why we don't touch
 *   `window.history` directly). A missing entry — meaning the server
 *   could not resolve the target — throws when invoked.
 * - **Refresh** — calls `deps.revalidate()`.
 * - **Action** — POSTs the payload via {@link submitViewInteraction}, then,
 *   on success, triggers `deps.revalidate()` (matching the module-action
 *   contract) and chains to a `next` interaction handler if the action
 *   declares one.
 */
export function buildInteractionHandlers<T extends InteractionMap>(
  interactions: T,
  deps: InteractionHandlerDeps,
): DeriveInteractionProps<T> {
  const handlers: Record<
    string,
    (data?: unknown) => void | Promise<unknown>
  > = {};

  for (const [key, interaction] of Object.entries(interactions)) {
    if (isNavigationInteraction(interaction)) {
      handlers[key] = (params?: unknown) => {
        applyResolvedNavigation(key, params, deps);
      };
    } else if (isRefreshInteraction(interaction)) {
      handlers[key] = () => {
        deps.revalidate();
      };
    } else if (isActionInteraction(interaction)) {
      handlers[key] = makeActionHandler(key, interaction, handlers, deps);
    } else {
      throw new Error(
        `Unknown interaction for key ${key}: ${String(interaction)}`,
      );
    }
  }

  // The runtime contract is that we visit every key of `interactions`, but TS
  // can't track that a `Record<string, fn>` we built exhaustively satisfies
  // the mapped-type `DeriveInteractionProps<T>`. Final assertion stands in
  // for the impossible-to-express invariant.
  return handlers as DeriveInteractionProps<T>;
}

// ============================================================================
// Action interactions
// ============================================================================

/**
 * `next` form on an `ActionInteraction`: either a static interaction key, a
 * dynamic callback that picks the key — or an {@link InlineNavigationTarget}
 * built from the result — from the action's result, or absent.
 *
 * The callback is typed against `unknown` here because the kernel only knows
 * the result via the action's runtime handler — the original
 * `TOutput` generic on `ActionInteraction<TSchema, TOutput>` is erased once
 * we narrow the union to a single interaction at runtime, so a `(result:
 * unknown) => string | InlineNavigationTarget` signature is the most honest
 * type at this seam.
 */
type NextValue =
  | string
  | ((result: unknown) => string | InlineNavigationTarget);

/**
 * Build the click handler for an action interaction. Extracted so the
 * inner closure doesn't capture the surrounding for-loop's `interaction`
 * binding (which would change as the loop advanced).
 *
 * Mirrors the module-action contract: post the payload, then revalidate
 * on success so any view-level data the action mutated is refetched.
 * After revalidation, optionally chain to a `next` interaction; the
 * chained handler may be sync (navigation/refresh) or async (another
 * action).
 */
function makeActionHandler(
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches isActionInteraction's narrowing; the action's TOutput is erased at this seam, so `next` resolves against `unknown` results
  interaction: ActionInteraction<any, any>,
  handlers: Record<string, (data?: unknown) => void | Promise<unknown>>,
  deps: InteractionHandlerDeps,
): (data?: unknown) => Promise<unknown> {
  return async (data?: unknown) => {
    const result = await submitViewInteraction({
      apiBaseUrl: deps.apiBaseUrl,
      viewId: deps.viewId,
      viewVersion: deps.viewVersion,
      actionName: key,
      data,
    });

    // Mirror the module-action contract: a successful action triggers a
    // route revalidation so any view-level data the action mutated is
    // refetched. The server-side action's `shouldRevalidate: false` will
    // suppress this in a follow-up; for now revalidate unconditionally
    // so refreshes are not silently dropped.
    deps.revalidate();

    const next = resolveNext(interaction.next, result);
    if (next === undefined) {
      return result;
    }

    if (typeof next === "string") {
      const nextHandler = handlers[next];
      if (nextHandler === undefined) {
        throw new Error(
          `Action interaction "${key}" chained to unknown interaction "${next}".`,
        );
      }
      // The chained interaction may be sync (navigation/refresh) or async
      // (another action); awaiting both is safe because `void` awaits to
      // `undefined` and `Promise<X>` awaits to `X`.
      await nextHandler();
    } else {
      // Inline navigation target returned from the callback — its destination
      // was computed from the typed result, so it never went through the
      // server's `resolvedInteractions`. Apply it directly.
      applyInlineNavigation(next, deps);
    }

    return result;
  };
}

/**
 * Resolve an `ActionInteraction.next` to the chained interaction key or an
 * inline navigation target, if any. The dynamic-next callback is invoked with
 * the action handler's result so the chain can branch on — or build a redirect
 * URL from — the server response. See {@link actionInteraction} for the
 * producing helper.
 */
function resolveNext(
  next: NextValue | undefined,
  result: unknown,
): string | InlineNavigationTarget | undefined {
  if (next === undefined) {
    return undefined;
  }
  if (typeof next === "string") {
    return next;
  }
  return next(result);
}

/**
 * Apply an {@link InlineNavigationTarget} returned from an action's `next`
 * callback. Only the two resolution-free variants are reachable here:
 * - external — `window.location.assign()` (guarded for SSR).
 * - back — `deps.goBack()` (React Router `navigate(-1)`; see
 *   {@link InteractionHandlerDeps.goBack} for why this routes through the
 *   router rather than `window.history.back()` directly).
 *
 * The `externalUrl` shape is validated at runtime: the callback's return type
 * is erased to `unknown` once the action's `TOutput` is gone (see
 * {@link NextValue}), so a callback that builds a malformed directive — e.g. a
 * missing/non-string URL slipping through `any` — is caught here with a clear
 * error rather than silently calling `window.location.assign(undefined)`.
 * Mirrors the unknown-chained-key guard in {@link makeActionHandler}.
 */
function applyInlineNavigation(
  target: InlineNavigationTarget,
  deps: InteractionHandlerDeps,
): void {
  if ("browserBack" in target) {
    deps.goBack();
    return;
  }
  if (typeof target.externalUrl !== "string" || target.externalUrl === "") {
    throw new Error(
      `Action interaction returned an inline external-navigation directive with an invalid externalUrl (${String(
        target.externalUrl,
      )}). Expected a non-empty string.`,
    );
  }
  if (typeof window !== "undefined") {
    window.location.assign(target.externalUrl);
  }
}

// ============================================================================
// Navigation
// ============================================================================

/**
 * Apply a server-resolved navigation target to the browser. Splits the three
 * variants in {@link ResolvedNavigationTarget}:
 * - `view` — compiles the URL from the route template + merged path params,
 *   then `navigate()` (with optional `?search` carry-over).
 * - `external` — `window.location.assign()`.
 * - `back` — `deps.goBack()` (React Router `navigate(-1)`; see
 *   {@link InteractionHandlerDeps.goBack} for why this routes through the
 *   router rather than `window.history.back()` directly).
 *
 * Throws when:
 * - The interaction key is not present in `deps.resolvedInteractions`
 *   (server could not resolve the target — surfaces a clear error here).
 * - The route template contains a `:name` token that is neither in the
 *   current request's path params nor in the caller-supplied params; the
 *   error message names the interaction and the missing key(s).
 */
function applyResolvedNavigation(
  key: string,
  callerParams: unknown,
  deps: InteractionHandlerDeps,
): void {
  const target = deps.resolvedInteractions[key];
  if (target === undefined) {
    throw new Error(
      `Navigation interaction "${key}" was not resolved by the server. ` +
        `This usually means the target viewId / branchId is not registered in the current flow.`,
    );
  }

  if (target.kind === "back") {
    deps.goBack();
    return;
  }

  if (target.kind === "external") {
    if (typeof window !== "undefined") {
      window.location.assign(target.url);
    }
    return;
  }

  // kind === "view"
  const merged = mergeNavigationParams(deps.currentPathParams, callerParams, key);
  const url = buildViewUrl(key, target, merged);
  const finalUrl =
    target.persistSearch === true ? mergeCurrentSearch(url) : url;
  deps.navigate(finalUrl);
}

/**
 * Combine the request's path params with whatever the caller handed the
 * navigation handler. The caller wins on collision so a row-level value
 * can override the page-level default (e.g. a list page's matched `userId`
 * being replaced by the row's id when the caller passes `{ userId: row.id }`).
 *
 * Returns a plain map of strings; rejects non-string entries up-front so
 * the regex-based substitution downstream can stay dumb.
 */
function mergeNavigationParams(
  currentPathParams: Record<string, string>,
  callerParams: unknown,
  key: string,
): Record<string, string> {
  const merged: Record<string, string> = { ...currentPathParams };
  if (callerParams === undefined || callerParams === null) {
    return merged;
  }
  if (typeof callerParams !== "object") {
    throw new Error(
      `Navigation interaction "${key}" was called with non-object params (${typeof callerParams}).`,
    );
  }
  for (const [name, value] of Object.entries(
    callerParams as Record<string, unknown>,
  )) {
    if (value === undefined) {
      continue;
    }
    if (typeof value !== "string") {
      throw new Error(
        `Navigation interaction "${key}" param "${name}" must be a string, got ${typeof value}.`,
      );
    }
    merged[name] = value;
  }
  return merged;
}

/**
 * Compile the full URL for a `view`-kind navigation target. The `:name`
 * tokens in `target.route` are filled from `params`; values are
 * URL-encoded so a caller value containing `/`, `?`, etc. doesn't break
 * the path. Query values of the form `":name"` are treated as path-param
 * references and resolved against `params`; references to missing keys
 * are dropped (matching the lenient stance the server originally took).
 *
 * Throws when any `:name` token in the route remains unresolved after
 * substitution — that's a real bug at the call site (caller forgot to
 * pass a required param) and a clear error is friendlier than navigating
 * to a malformed URL.
 */
function buildViewUrl(
  key: string,
  target: Extract<ResolvedNavigationTarget, { kind: "view" }>,
  params: Record<string, string>,
): string {
  const path = compileRoute(key, target.route, params);
  const queryStr = compileQuery(target.query, params);
  return queryStr.length > 0 ? `${path}?${queryStr}` : path;
}

/**
 * Substitute `:name` tokens with URL-encoded values from `params` using
 * `path-to-regexp`'s `compile` — the same library the server's
 * resolution hierarchy uses to build redirect URLs, so the
 * tokenization, encoding, and missing-param semantics are identical on
 * both sides of the wire.
 *
 * Re-throws compile errors with the navigation key prepended so the
 * stack trace points at the offending interaction rather than a bare
 * "Missing parameter: userId".
 */
function compileRoute(
  key: string,
  route: string,
  params: Record<string, string>,
): string {
  try {
    return compile(route, { encode: encodeURIComponent })(params);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Navigation interaction "${key}" failed to compile route "${route}": ${message}`,
    );
  }
}

/**
 * Build the `?…` portion of the URL from a verbatim query map. Each value
 * is either a literal (passes through) or a `:name` reference (resolved
 * from `params`; dropped if unresolved). Returns the empty string when
 * nothing survives — callers can use that to skip the leading `?`.
 */
function compileQuery(
  query: Record<string, string> | undefined,
  params: Record<string, string>,
): string {
  if (!query || Object.keys(query).length === 0) {
    return "";
  }
  const search = new URLSearchParams();
  for (const [qkey, raw] of Object.entries(query)) {
    const resolved = resolveQueryValue(raw, params);
    if (resolved !== null) {
      search.set(qkey, resolved);
    }
  }
  return search.toString();
}

/**
 * Resolve a single `query` map value: `":name"` references `params[name]`
 * (and is dropped if missing); anything else is passed through verbatim.
 *
 * Path-param-style placeholders are the only template syntax supported
 * here; anything richer would need its own parser and is out of scope.
 */
function resolveQueryValue(
  value: string,
  params: Record<string, string>,
): string | null {
  if (value.startsWith(":") && value.length > 1) {
    const refName = value.slice(1);
    return params[refName] ?? null;
  }
  return value;
}

/**
 * Carry the current URL's search params onto a freshly-compiled view URL,
 * letting the interaction's own query string win on key collision (the
 * interaction's `query` map represents intent for *this* navigation, so
 * those values should override whatever happens to be in
 * `window.location.search`).
 */
function mergeCurrentSearch(url: string): string {
  if (typeof window === "undefined") {
    return url;
  }
  const currentSearch = window.location.search;
  if (!currentSearch || currentSearch.length <= 1) {
    return url;
  }

  const [path, existingQuery = ""] = url.split("?", 2);
  const merged = new URLSearchParams(currentSearch);
  for (const [k, v] of new URLSearchParams(existingQuery)) {
    merged.set(k, v);
  }
  const serialized = merged.toString();
  return serialized.length > 0 ? `${path}?${serialized}` : (path ?? "");
}
